import { motion } from "framer-motion";
import {
  ClipboardList, Droplet, Truck, FlaskConical, FileBarChart2, Sparkles,
} from "lucide-react";
import { STEPS } from "@/lib/siteData";

// Phase 2L-A36 — Each step now carries a small, calm lucide icon
// matched to the existing visual language. Inline SVG only, zero KB,
// no http requests, no CLS, no impact on mobile performance.
const STEP_ICONS = [
  ClipboardList,    // 01 Book Online
  Droplet,          // 02 Collect Sample
  Truck,            // 03 Deliver To Lab
  FlaskConical,     // 04 Laboratory Testing
  FileBarChart2,    // 05 Results & Summary
  Sparkles,         // 06 Recommendations
];

export const HowItWorks = () => {
  return (
    <section id="how" className="relative py-12 lg:py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">How It Works</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.05]">
              From sample to <span className="water-text">answers</span> — in six clear steps.
            </h2>
            <p className="mt-5 text-lg text-slate-600 max-w-md">
              A streamlined process designed to give you peace of mind, fast. Most reports turn around within business days.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              {/* Vertical connector */}
              <div className="absolute left-[27px] top-2 bottom-2 w-px bg-gradient-to-b from-cyan-400 via-sky-400 to-transparent" />

              <div className="space-y-6">
                {STEPS.map((s, i) => {
                  const Icon = STEP_ICONS[i] || ClipboardList;
                  return (
                  <motion.div
                    key={s.n}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="relative flex items-start gap-5 group"
                  >
                    <div className="relative z-10 flex-shrink-0 w-14 h-14 rounded-2xl bg-white border-2 border-slate-900 flex items-center justify-center font-display font-bold text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-all duration-300">
                      {s.n}
                    </div>
                    <div className="flex-1 pb-2 flex items-start gap-4">
                      <div className="flex-1">
                        <h3 className="font-display text-xl font-semibold text-slate-900">
                          {s.title}
                          {i === 5 && (
                            <span className="ml-2 text-[10px] uppercase tracking-[0.15em] font-bold text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded">
                              Optional
                            </span>
                          )}
                        </h3>
                        <p className="mt-1 text-base text-slate-600 leading-relaxed">{s.desc}</p>
                      </div>
                      <div
                        className="hidden sm:flex flex-shrink-0 w-12 h-12 rounded-xl bg-cyan-50/70 border border-cyan-100 items-center justify-center text-cyan-700 group-hover:bg-cyan-100 group-hover:border-cyan-200 transition-all"
                        aria-hidden="true"
                      >
                        <Icon className="w-5 h-5" strokeWidth={1.6} />
                      </div>
                    </div>
                  </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
